/**
 * 实名三要素验证 + 系统检测流程 Hook
 * - 身份验证：信息步提交时内联触发，带网络错误重试与连续业务失败引导人工审核
 * - 系统检测：进入检测步自动触发，按结果分流（登录引导/合并/绑定/释放/换号短信/新用户）
 */
import type { Dispatch, SetStateAction } from 'react';
import { verifyIdentity, checkSystemIdentity } from '../../../../../api/register';
import { useRegisterStore } from '../../../../../stores/registerStore';
import type { RegisterFormData } from '../../../../../stores/registerStore';
import type { ReleaseFlowState, ToastType } from '../types';

export function useVerificationFlow(
  formData: RegisterFormData,
  showToast: (message: string, type?: ToastType) => void,
  verifyFailCountRef: { current: number },
  setReleaseFlow: Dispatch<SetStateAction<ReleaseFlowState>>,
) {
  // 从store获取状态和动作（与主控制器共享同一 zustand 实例）
  const {
    clearAllErrors,
    setIdentityVerificationLoading,
    setIdentityVerified,
    setIdentityVerificationError,

    setManualApplicationReason,
    setShowManualApplication,
    setSystemCheckLoading,
    setSystemCheckResult,
    setShowIdentityMerge,
    setShowCustomerBinding,
    nextStep,
  } = useRegisterStore();

  // 三要素验证处理（信息步内联触发，带重试机制）
  const handleIdentityVerification = async (retryCount = 0) => {
    setIdentityVerificationLoading(true);
    clearAllErrors();

    try {
      const response = await verifyIdentity({
        fullName: formData.fullName,
        idCard: formData.idCard,
        phone: formData.phone,
        backupPhone: formData.backupPhone || undefined
      });

      if (response.verified) {
        verifyFailCountRef.current = 0;
        setIdentityVerified(true);
        setIdentityVerificationError(null);
        showToast('实名核验通过', 'success');
        // 验证成功后自动进入检测步
        setTimeout(() => {
          nextStep();
        }, 800);
      } else {
        setIdentityVerified(false);
        setIdentityVerificationError(response.message || '身份验证失败');

        // 连续 3 次业务核验失败：引导提交人工审核申请
        verifyFailCountRef.current += 1;
        if (verifyFailCountRef.current >= 3) {
          verifyFailCountRef.current = 0;
          setManualApplicationReason('多次实名核验未通过，需要客服人工核对后处理');
          setShowManualApplication(true);
          showToast('多次实名核验未通过，请提交人工审核申请', 'info');
        } else {
          showToast(response.message || '身份验证失败', 'error');
        }
      }
    } catch (error) {
      setIdentityVerified(false);
      setIdentityVerificationError(error instanceof Error ? error.message : '身份验证失败');


      // 业务失败（后端 success:false 经 unwrapResponse 抛出）：视同核验未通过，计入连续失败
      if ((error as { isBusinessError?: boolean }).isBusinessError) {
        verifyFailCountRef.current += 1;
        if (verifyFailCountRef.current >= 3) {
          verifyFailCountRef.current = 0;
          setManualApplicationReason('多次实名核验未通过，需要客服人工核对后处理');
          setShowManualApplication(true);
          showToast('多次实名核验未通过，请提交人工审核申请', 'info');
        } else {
          showToast(error instanceof Error ? error.message : '身份验证失败', 'error');
        }
        return;
      }

      // 重试逻辑（仅网络错误）
      if (retryCount < 2) {
        showToast('网络错误，正在重试...', 'info');
        setTimeout(() => {
          handleIdentityVerification(retryCount + 1);
        }, 1000);
      } else {
        showToast('身份验证失败，请稍后重试', 'error');
      }
    } finally {
      setIdentityVerificationLoading(false);
    }
  };

  // 系统检测处理（带重试机制）
  const handleSystemCheck = async (retryCount = 0) => {
    setSystemCheckLoading(true);

    try {
      const response = await checkSystemIdentity({
        fullName: formData.fullName,
        phone: formData.phone,
        idCard: formData.idCard,
        backupPhone: formData.backupPhone || undefined
      });

      setSystemCheckResult(response);

      // 根据检测结果处理
      if (response.requiresReview) {
        const reasonText =
          response.reviewReason === 'idcard_conflict'
            ? '您填写的身份证号在系统内已关联其他姓名与联系方式，需要客服人工核对后处理'
            : response.reviewReason === 'contact_conflict'
            ? '您填写的邮箱或用户名在系统内已被占用，需要客服人工核对后处理'
            : '您的注册信息与系统内记录存在冲突，需要客服人工核对后处理';
        setManualApplicationReason(reasonText);
        setShowManualApplication(true);
        showToast('检测到身份冲突，请提交人工复核', 'info');
      } else if (response.isRegistered) {
        showToast('您已注册，请直接登录', 'info');
      } else if (response.multipleIdentities) {
        setShowIdentityMerge(true);
        showToast('检测到多重身份，需要进行身份合并', 'info');
      } else if (response.phoneChanged) {
        // 升格换号必须优先于客户档案认领分支：旧手机号用户已无法接收短信，
        // 先弹认领引导会陷入「认领失败→重新检测→再弹认领」死循环；
        // 换号验证码随注册提交（smsCode）由后端校验
        showToast('检测到换号注册，请完成新手机号短信验证', 'info');
      } else if (response.exists && !response.isRegistered) {
        setShowCustomerBinding(true);
        showToast(
          response.phoneOnlyMatch
            ? '检测到该手机号已关联客户档案，请比对档案照片确认归属'
            : '检测到您的客户信息，需要绑定',
          'info'
        );
      } else if (response.releaseRequired) {
        setReleaseFlow((prev) => ({ ...prev, token: null, code: '' }));
        // 只点名状态，整句说明由服务端随 phoneConflictHint.releaseNotice 下发、就地显示在检测步卡片里
        showToast('需要验证手机号归属', 'info');

      } else {
        if (response.phoneRecycled) {
          showToast('检测完成（您的手机号曾被系统内其他联系人登记，不影响注册）', 'info');
        } else {
          showToast('系统检测完成', 'success');
        }
      }
    } catch (error) {
      if (retryCount < 2) {
        showToast('网络错误，正在重试...', 'info');
        setTimeout(() => {
          handleSystemCheck(retryCount + 1);
        }, 1000);
      } else {
        showToast(error instanceof Error ? error.message : '系统检测失败，请稍后重试', 'error');
      }
    } finally {
      setSystemCheckLoading(false);
    }
  };

  return {
    handleIdentityVerification,
    handleSystemCheck,
  };
}